import React, { useRef } from "react";
import { Box, Typography, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useSearchParams } from "react-router-dom";
import usePublicTestStepResultQuery from "../../useQuery/usePublicTestStepResultQuery";
import StatusBar from "../Studio/StatusBar";
import PublicTestStepCard from "./PublicTestStepCard";

const ReportContainer = styled(Paper)({
  backgroundColor: "#f5f7fa",
  minHeight: "100vh",
  borderRadius: 0,
  padding: "16px",
  width: "100%",
});

const ReportContent = styled(Box)({
  width: "100%",
  maxWidth: "100%",
  margin: "0 auto",
});

const ReportHeader = styled(Box)({
  backgroundColor: "#ffffff",
  padding: "20px",
  borderRadius: "8px",
  boxShadow: "0px 43px 62.4px 0px rgba(0, 0, 0, 0.09)",
  marginBottom: "20px",
  width: "100%",
  borderTop: "2px solid #2550d1",
});

const ReportTitle = styled(Typography)({
  color: "#2550d1",
  fontWeight: 700,
  fontSize: "28px",
  marginBottom: "16px",
  letterSpacing: "0.5px",
});

const TestCaseName = styled(Typography)({
  color: "#232d3d",
  fontSize: "20px",
  fontWeight: 500,
  padding: "16px",
  backgroundColor: "#f8f9fa",
  borderRadius: "8px",
  border: "1px solid #e2e6f3",
  marginBottom: "20px",
});

const StatusBarContainer = styled(Box)({
  backgroundColor: "#ffffff",
  padding: "20px",
  borderRadius: "8px",
  boxShadow: "0px 43px 62.4px 0px rgba(0, 0, 0, 0.09)",
  marginBottom: "20px",
  width: "100%",
  borderTop: "2px solid #2550d1",
});

const StepsContainer = styled(Box)({
  backgroundColor: "#ffffff",
  padding: "20px",
  borderRadius: "8px",
  boxShadow: "0px 43px 62.4px 0px rgba(0, 0, 0, 0.09)",
  width: "100%",
  borderTop: "2px solid #2550d1",
});

const StepsTitle = styled(Typography)({
  color: "#2550d1",
  fontWeight: 600,
  fontSize: "20px",
  marginBottom: "20px",
  paddingBottom: "12px",
  borderBottom: "2px solid #e2e6f3",
});

const StepsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: "20px",
  marginTop: "16px",
  width: "100%",
});

const PublicTestResults = () => {
  const { companyId, testCaseId, resultCaseId, token } = useParams();
  const [searchParams] = useSearchParams();
  const tcrid = resultCaseId || searchParams.get("tcrid");
  const containerRef = useRef(null);

  const {
    data: testResult,
    isLoading,
    isSuccess,
  } = usePublicTestStepResultQuery({
    tcrid,
    companyId,
    token,
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <Typography>Loading test results...</Typography>
      </Box>
    );
  }

  if (!testResult || !testResult.test_step_data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <Typography>No test results found or invalid access token.</Typography>
      </Box>
    );
  }

  const { test_case_data, test_step_data } = testResult;

  return (
    <ReportContainer>
      <ReportContent>
        <ReportHeader>
          <ReportTitle>Test Execution Report</ReportTitle>
          <TestCaseName>
            {test_case_data?.test_case_name || "Test Case Name Not Available"}
          </TestCaseName>
        </ReportHeader>

        <StatusBarContainer>
          <StatusBar testcaseid={testCaseId} testcaseresultId={tcrid} data={test_case_data} />
        </StatusBarContainer>

        <StepsContainer>
          <StepsTitle>Test Steps Execution Details</StepsTitle>
          <StepsGrid ref={containerRef}>
            {test_step_data.map((item, index) => (
              <PublicTestStepCard item={item} key={index} />
            ))}
          </StepsGrid>
        </StepsContainer>
      </ReportContent>
    </ReportContainer>
  );
};

export default PublicTestResults;
